import React from 'react';
import { Box, Typography } from '@mui/material';
import { Bucket, RunRow } from '../../services/executionStatsService';
import { useBucketColours, BUCKET_LABELS, count, duration, relativeTime } from './executionStatsVocabulary';
import { dsVar, ink } from '../../theme/tokens';

/**
 * How a run's status and errors are shown, shared by the run list in the
 * Overview drawer and the run log table so the two never disagree.
 *
 * Styled with sx, not Tailwind classes: these render inside portalled drawers
 * too, where Tailwind's important selector doesn't reach.
 */

/**
 * What makes two errors "the same" for grouping: the first line, with ids,
 * timestamps and long numbers blanked, so a timeout that names a different
 * request each time still reads as one cause.
 */
export const errorSignature = (message: string): string =>
    message
        .split('\n')[0]
        .trim()
        .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '…')
        .replace(/\b[0-9a-f]{12,}\b/gi, '…')
        .replace(/\d{4}-\d{2}-\d{2}[T ][\d:.]+Z?/g, '…')
        .replace(/\d{3,}/g, '…');

export interface ErrorGroup {
    signature: string;
    /** The newest run's own message, first line, for display. */
    example: string;
    runs: number;
    latestAt: string | null;
}

/** Errors among these runs, most frequent first; runs arrive newest first. */
/** Anything with an error and a start: a run, or one of its steps. */
type WithError = Pick<RunRow, 'errorMessage' | 'startedAt'>;

export const groupErrors = (runs: WithError[]): ErrorGroup[] => {
    const groups = new Map<string, ErrorGroup>();
    runs.forEach((run) => {
        if (!run.errorMessage) return;
        const signature = errorSignature(run.errorMessage);
        const group = groups.get(signature);
        if (group) group.runs += 1;
        else groups.set(signature, {
            signature,
            example: run.errorMessage.split('\n')[0].trim(),
            runs: 1,
            latestAt: run.startedAt
        });
    });
    return [...groups.values()].sort((a, b) => b.runs - a.runs);
};

const MAX_ERROR_GROUPS = 3;

/**
 * The causes behind these runs, before the runs themselves.
 *
 * Grouped from the page in hand, and labelled so: counting every run's error
 * would take an API that groups them, and this answers "is it one problem or
 * many" for the newest runs, which is the question a fix starts with. Shown
 * only when at least two runs share a cause.
 */
export const ErrorGroups: React.FC<{ runs: WithError[]; total: number }> = ({ runs, total }) => {
    const groups = groupErrors(runs);
    if (!groups.some((group) => group.runs > 1)) return null;
    const withErrors = groups.reduce((sum, group) => sum + group.runs, 0);
    return (
        <Box sx={{ mb: 1.5, p: 1.25, borderRadius: '8px', border: `1px solid ${dsVar('line')}`, backgroundColor: dsVar('page', 0.4) }}>
            <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-2'), fontWeight: 600, mb: 0.75 }}>
                {total > runs.length
                    ? `Most common errors in the newest ${count(runs.length)} runs`
                    : `Errors across these ${count(withErrors)} runs`}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {groups.slice(0, MAX_ERROR_GROUPS).map((group) => (
                    <Box key={group.signature} sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: ink('#F87171'), flex: 'none' }}>
                            {count(group.runs)} ×
                        </Typography>
                        <Typography
                            title={group.example}
                            sx={{
                                fontSize: '0.6875rem', color: ink('#FCA5A5'), fontFamily: '"JetBrains Mono", monospace', minWidth: 0, flex: 1,
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                            }}
                        >
                            {group.example}
                        </Typography>
                        <Typography sx={{ fontSize: '0.625rem', color: dsVar('fg-muted'), flex: 'none' }}>
                            latest {relativeTime(group.latestAt)}
                        </Typography>
                    </Box>
                ))}
                {groups.length > MAX_ERROR_GROUPS && (
                    <Typography sx={{ fontSize: '0.625rem', color: dsVar('fg-muted') }}>
                        and {count(groups.length - MAX_ERROR_GROUPS)} other {groups.length - MAX_ERROR_GROUPS === 1 ? 'error' : 'errors'}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export const when = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

export const StatusPill: React.FC<{ bucket: Bucket | null; status: string | null }> = ({ bucket, status }) => {
    const bucketColours = useBucketColours();
    return (
    <Box
        component="span"
        title={status ? `Recorded as "${status}"` : undefined}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.6875rem', color: dsVar('fg-soft'), whiteSpace: 'nowrap' }}
    >
        <Box
            component="span"
            sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: bucket ? bucketColours[bucket] : '#475569' }}
        />
        {bucket ? BUCKET_LABELS[bucket] : status || 'Unknown'}
    </Box>
    );
};

/**
 * How long a run or step took, or for one without a finish, how long it has
 * been going and what that means for its bucket.
 */
export const elapsed = (
    item: Pick<RunRow, 'startedAt' | 'completedAt' | 'durationMs' | 'bucket'>,
    now = Date.now()
): string => {
    if (item.completedAt) return duration(item.durationMs);
    const started = item.startedAt ? Date.parse(item.startedAt) : NaN;
    if (!Number.isFinite(started)) return 'No finish recorded';
    const since = duration(Math.max(0, now - started));
    if (item.bucket === 'running') return `Running for ${since}`;
    if (item.bucket === 'stalled') return `No finish after ${since}`;
    if (item.bucket === 'pendingReview') return `Waiting ${since}`;
    return 'No finish recorded';
};
